import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { MenuProps } from 'antd';
import {
  Button,
  Card,
  Col,
  Checkbox,
  Dropdown,
  Descriptions,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message
} from 'antd';
import { EyeOutlined, PhoneOutlined, PlusOutlined, RobotOutlined } from '@ant-design/icons';
import { api } from './api';
import { formatCvrNumber, formatDateTimeFull, formatDateTimeShort } from './constants';
import { CompanyAiSummary, CompanySearchResult, CvrSummary, Label } from './types';
import LeadDetailPage from './LeadDetailPage';
import { useSearchParams } from 'react-router-dom';

const { Title, Text, Paragraph } = Typography;

type Props = {
  canManage?: boolean;
  onLeadCreated?: () => void | Promise<void>;
};

type SearchFormValues = {
  name?: string;
  cvr?: string;
  status?: string;
  branche?: string;
  postal?: string;
  municipality?: string;
  includeExistingLeads?: boolean;
  minEmployees?: number | null;
  maxEmployees?: number | null;
  limit?: number;
};

type NormalizedSearchValues = {
  name: string;
  cvr: string;
  status: string;
  branche: string;
  postal: string;
  municipality: string;
  includeExistingLeads: boolean;
  minEmployees: number | null;
  maxEmployees: number | null;
  limit: number;
};

const DEFAULT_PAGE_SIZE = 10;
const MAX_PAGE_SIZE = 1000;
const DEFAULT_LABEL_COLOR = '#d3d3d3';

type CompanyUser = {
  id: string;
  full_name: string | null;
  email: string | null;
  role?: string | null;
};

function onlyDigits(value: string | null | undefined) {
  if (!value) {
    return '';
  }
  return value.replace(/\D+/g, '');
}

function normalizePageSize(value: unknown) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return DEFAULT_PAGE_SIZE;
  }
  return Math.min(Math.floor(parsed), MAX_PAGE_SIZE);
}

function normalizeEmployeeBound(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  const floored = Math.floor(parsed);
  if (floored < 0) {
    return null;
  }
  return floored;
}

function normalizeSearchValues(values: SearchFormValues): NormalizedSearchValues {
  const brancheRaw = values.branche;
  return {
    name: values.name?.trim() ?? '',
    cvr: values.cvr?.trim() ?? '',
    status: values.status?.trim() ?? '',
    branche: typeof brancheRaw === 'string' ? brancheRaw.trim() : '',
    postal: values.postal?.trim() ?? '',
    municipality: values.municipality?.trim() ?? '',
    includeExistingLeads: Boolean(values.includeExistingLeads),
    minEmployees: normalizeEmployeeBound(values.minEmployees),
    maxEmployees:
      normalizeEmployeeBound(
        typeof values.maxEmployees === 'number' || values.maxEmployees === null
          ? values.maxEmployees
          : 999
      ) ?? 999,
    limit: normalizePageSize(values.limit)
  };
}

function hasSearchCriteria(values: NormalizedSearchValues) {
  return Boolean(
    values.name ||
      values.cvr ||
      values.status ||
      values.branche ||
      values.postal ||
      values.municipality ||
      values.minEmployees !== null ||
      values.maxEmployees !== null
  );
}

function getValuesFromParams(params: URLSearchParams): SearchFormValues {
  return {
    name: params.get('name') || undefined,
    cvr: params.get('cvr') || undefined,
    status: params.get('status') || undefined,
    branche: params.get('branche') || undefined,
    postal: params.get('postal') || undefined,
    municipality: params.get('municipality') || undefined,
    includeExistingLeads: params.get('includeExistingLeads') === 'true',
    minEmployees: (() => {
      const parsed = normalizeEmployeeBound(params.get('minEmployees'));
      return parsed === null ? undefined : parsed;
    })(),
    maxEmployees: (() => {
      const parsed = normalizeEmployeeBound(params.get('maxEmployees'));
      return parsed === null ? undefined : parsed;
    })(),
    limit: params.get('limit') ? normalizePageSize(params.get('limit')) : undefined
  };
}

function buildQueryParams(values: NormalizedSearchValues) {
  const params = new URLSearchParams();
  if (values.name) {
    params.set('name', values.name);
  }
  if (values.cvr) {
    params.set('cvr', values.cvr);
  }
  if (values.status) {
    params.set('status', values.status);
  }
  if (values.branche) {
    params.set('branche', values.branche);
  }
  if (values.postal) {
    params.set('postal', values.postal);
  }
  if (values.municipality) {
    params.set('municipality', values.municipality);
  }
  if (values.minEmployees !== null) {
    params.set('minEmployees', String(values.minEmployees));
  }
  if (values.maxEmployees !== null) {
    params.set('maxEmployees', String(values.maxEmployees));
  }
  if (values.includeExistingLeads) {
    params.set('includeExistingLeads', 'true');
  }
  if (values.limit && values.limit !== DEFAULT_PAGE_SIZE) {
    params.set('limit', String(values.limit));
  }
  return params;
}

function getPageFromParams(params: URLSearchParams) {
  const raw = params.get('page');
  if (!raw) {
    return 1;
  }
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return 1;
  }
  return parsed;
}

function applyPageParam(params: URLSearchParams, page: number) {
  if (page > 1) {
    params.set('page', String(page));
  } else {
    params.delete('page');
  }
}

function buildAddress(summary: CvrSummary | null) {
  if (!summary) {
    return '';
  }
  const segments: string[] = [];
  const street: string[] = [];
  if (summary.vejnavn) {
    street.push(summary.vejnavn.trim());
  }
  const from = summary.husnummer_fra?.trim();
  const to = summary.husnummer_til?.trim();
  if (from && to && from !== to) {
    street.push(`${from}-${to}`);
  } else if (from) {
    street.push(from);
  } else if (to) {
    street.push(to);
  }
  if (summary.etage) {
    street.push(`${summary.etage}.`);
  }
  if (summary.doer) {
    street.push(summary.doer);
  }
  if (street.length) {
    segments.push(street.join(' ').replace(/\s+/g, ' ').trim());
  }
  if (summary.supplerende_bynavn) {
    segments.push(summary.supplerende_bynavn.trim());
  }
  const postal: string[] = [];
  if (summary.postnummer) {
    postal.push(summary.postnummer.trim());
  }
  if (summary.postdistrikt) {
    postal.push(summary.postdistrikt.trim());
  }
  if (postal.length) {
    segments.push(postal.join(' ').trim());
  }
  if (summary.kommunenavn) {
    segments.push(summary.kommunenavn.trim());
  }
  return segments.join(', ');
}

function formatUpdated(value: string | null | undefined) {
  if (!value) {
    return '—';
  }
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return new Intl.DateTimeFormat('da-DK', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(date);
  } catch (error) {
    console.error('Failed to format timestamp', error);
    return value;
  }
}

function statusColor(status: string | null | undefined) {
  if (!status) {
    return 'default';
  }
  const normalized = status.toLowerCase();
  if (normalized.includes('aktiv')) {
    return 'green';
  }
  if (normalized.includes('under opløsning') || normalized.includes('opløsning')) {
    return 'orange';
  }
  if (normalized.includes('opløst') || normalized.includes('konkurs')) {
    return 'red';
  }
  return 'blue';
}

export default function CompanySearchPage({ canManage = false, onLeadCreated }: Props) {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<CompanySearchResult[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [labels, setLabels] = useState<Label[]>([]);
  const [labelsLoading, setLabelsLoading] = useState(false);
  const [assigningLabelFor, setAssigningLabelFor] = useState<string | null>(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<CvrSummary | null>(null);
  const [detailCvr, setDetailCvr] = useState<string | null>(null);
  const [aiModalVisible, setAiModalVisible] = useState(false);
  const [aiModalLoading, setAiModalLoading] = useState(false);
  const [aiSummary, setAiSummary] = useState<CompanyAiSummary | null>(null);
  const [aiSummaryCvr, setAiSummaryCvr] = useState<string | null>(null);
  const [aiAnalyzingFor, setAiAnalyzingFor] = useState<string | null>(null);
  const [usersModalOpen, setUsersModalOpen] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([]);
  const [usersCompany, setUsersCompany] = useState<{ id: string; name: string | null; cvr: string } | null>(null);
  const [createUserLoading, setCreateUserLoading] = useState(false);
  const [userForm] = Form.useForm();
  const aiModalSections = useMemo(() => {
    const raw = aiSummary?.ai_summary_text;
    if (!raw) {
      return [] as string[];
    }
    const trimmed = raw.trim();
    if (!trimmed) {
      return [] as string[];
    }
    return trimmed
      .split(/\n{2,}/)
      .map((block: string) => block.trim())
      .filter(Boolean);
  }, [aiSummary?.ai_summary_text]);

  const currentUsersCompanyId = usersCompany?.id;

  const resolveCompanyId = useCallback((record: CompanySearchResult) => {
    return record.companyId || (record as any).virksomhed_id || record.cvrnummer;
  }, []);

  const openUsersModal = useCallback(
    (record: CompanySearchResult) => {
      const companyId = resolveCompanyId(record);
      if (!companyId) {
        message.error('Mangler firma-id for brugere');
        return;
      }
      setUsersCompany({ id: companyId, name: record.navn || null, cvr: record.cvrnummer });
      setUsersModalOpen(true);
    },
    [resolveCompanyId]
  );

  const closeUsersModal = useCallback(() => {
    setUsersModalOpen(false);
    setUsersCompany(null);
    setCompanyUsers([]);
    userForm.resetFields();
  }, [userForm]);

  const loadCompanyUsers = useCallback(async () => {
    if (!usersCompany) return;
    setUsersLoading(true);
    try {
      const res = await api(`/api/companies/${encodeURIComponent(usersCompany.id)}/users`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke hente brugere');
      }
      const data = (await res.json()) as CompanyUser[];
      setCompanyUsers(data);
    } catch (error: any) {
      console.error('Failed to load company users', error);
      message.error(error?.message || 'Kunne ikke hente brugere');
    } finally {
      setUsersLoading(false);
    }
  }, [usersCompany]);

  useEffect(() => {
    if (usersModalOpen) {
      void loadCompanyUsers();
    }
  }, [usersModalOpen, loadCompanyUsers]);

  const handleCreateUser = useCallback(async () => {
    if (!usersCompany) return;
    try {
      const values = await userForm.validateFields();
      setCreateUserLoading(true);
      const payload = {
        full_name: values.full_name?.trim() || null,
        email: values.email?.trim() || null,
        role: values.role || null
      };
      const res = await api(`/api/companies/${encodeURIComponent(usersCompany.id)}/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke oprette bruger');
      }
      message.success('Bruger oprettet');
      userForm.resetFields();
      await loadCompanyUsers();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      console.error('Create user failed', error);
      message.error(error?.message || 'Kunne ikke oprette bruger');
    } finally {
      setCreateUserLoading(false);
    }
  }, [usersCompany, userForm, loadCompanyUsers]);

  const aiModalGeneratedLabel = useMemo(() => {
    if (!aiSummary?.ai_summary_generated_at) {
      return null;
    }
    return formatDateTimeFull(aiSummary.ai_summary_generated_at);
  }, [aiSummary?.ai_summary_generated_at]);
  const [leadCreatingFor, setLeadCreatingFor] = useState<string | null>(null);
  const [quickCallTarget, setQuickCallTarget] = useState<string | null>(null);
  const [brancheOptions, setBrancheOptions] = useState<
    { code: string | null; text: string }[]
  >([]);
  const [brancheLoading, setBrancheLoading] = useState(false);
  const [form] = Form.useForm();
  const [leadDetailVisible, setLeadDetailVisible] = useState(false);
  const [leadDetailId, setLeadDetailId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const lastExecutedQueryRef = useRef<string>('');
  const searchParamsKey = searchParams.toString();
  const paramsValues = useMemo(
    () => getValuesFromParams(new URLSearchParams(searchParamsKey)),
    [searchParamsKey]
  );
  const leadDetailLocationState = useMemo(
    () => (searchParamsKey ? { fromCompanySearch: searchParamsKey } : undefined),
    [searchParamsKey]
  );
  const [currentPage, setCurrentPage] = useState(() =>
    getPageFromParams(new URLSearchParams(searchParamsKey))
  );
  const [pageSize, setPageSize] = useState(() => paramsValues.limit ?? DEFAULT_PAGE_SIZE);
  const pageFromParams = useMemo(
    () => getPageFromParams(new URLSearchParams(searchParamsKey)),
    [searchParamsKey]
  );

  const loadLabels = useCallback(async () => {
    setLabelsLoading(true);
    try {
      const res = await api('/api/labels');
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke hente labels');
      }
      const data = (await res.json()) as Label[];
      setLabels(
        [...data].sort((a, b) => a.name.localeCompare(b.name, 'da', { sensitivity: 'base' }))
      );
    } catch (error: any) {
      console.error('Failed to load labels', error);
      message.error(error?.message || 'Kunne ikke hente labels');
    } finally {
      setLabelsLoading(false);
    }
  }, []);

  const labelMenuItems = useMemo<MenuProps['items']>(
    () =>
      labels.map((label) => ({
        key: label.id,
        label: (
          <Space size={4} align="center">
            <Tag
              color={label.color || DEFAULT_LABEL_COLOR}
              style={{ minWidth: 72, textAlign: 'center', fontWeight: 600 }}
            >
              {label.name}
            </Tag>
            {label.hideInSearch ? <Text type="secondary">Skjul i søgeresultat</Text> : null}
          </Space>
        )
      })),
    [labels]
  );

  useEffect(() => {
    setCurrentPage((prev) => (prev === pageFromParams ? prev : pageFromParams));
  }, [pageFromParams]);

  useEffect(() => {
    let cancelled = false;

    async function loadBranches() {
      setBrancheLoading(true);
      try {
        const res = await api('/api/companies/branche-options');
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Kunne ikke hente brancher');
        }
        const data = (await res.json()) as { code: string | null; text: string }[];
        if (!cancelled) {
          setBrancheOptions(data.filter((item) => Boolean(item?.text?.trim())));
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load branches', error);
          message.warning('Kunne ikke hente brancheliste');
        }
      } finally {
        if (!cancelled) {
          setBrancheLoading(false);
        }
      }
    }

    loadBranches();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    void loadLabels();
  }, [loadLabels]);

  const performSearch = useCallback(
    async (
      formValues: SearchFormValues,
      options?: { skipUpdateParams?: boolean; page?: number }
    ) => {
      const normalized = normalizeSearchValues(formValues);
      const nextPage = options?.page ?? 1;
      setPageSize(normalized.limit);

      if (!hasSearchCriteria(normalized)) {
        message.warning('Angiv mindst ét søgekriterie');
        setResults([]);
        if (!options?.skipUpdateParams) {
          setSearchParams({}, { replace: true });
          lastExecutedQueryRef.current = '';
        }
        setCurrentPage(1);
        return;
      }

      if (normalized.name && normalized.name.length < 3) {
        message.warning('Navn skal mindst indeholde 3 tegn');
        return;
      }

      if (normalized.cvr) {
        const digits = normalized.cvr.replace(/\D+/g, '');
        if (digits.length < 3) {
          message.warning('CVR-nummer skal mindst indeholde 3 cifre');
          return;
        }
      }

      if (normalized.status && normalized.status.length < 2) {
        message.warning('Status skal mindst indeholde 2 tegn');
        return;
      }

      if (normalized.municipality && normalized.municipality.length < 2) {
        message.warning('Kommune skal mindst indeholde 2 tegn');
        return;
      }

      if (
        normalized.minEmployees !== null &&
        normalized.maxEmployees !== null &&
        normalized.minEmployees > normalized.maxEmployees
      ) {
        message.warning('Mindste antal ansatte må ikke være større end største antal ansatte');
        return;
      }

      const params = buildQueryParams(normalized);
      const paramsWithPage = new URLSearchParams(params);
      applyPageParam(paramsWithPage, nextPage);
      lastExecutedQueryRef.current = paramsWithPage.toString();
      if (!options?.skipUpdateParams) {
        setSearchParams(paramsWithPage);
      }
      setCurrentPage(nextPage);

      const queryParams = new URLSearchParams(paramsWithPage);
      queryParams.set('limit', String(normalized.limit));
      const queryString = queryParams.toString();

      setLoading(true);
      try {
        const res = await api(`/api/companies/search?${queryString}`);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Kunne ikke søge efter virksomheder');
        }

        const payload = (await res.json()) as
          | {
              items?: CompanySearchResult[];
              total?: number | string | null;
              results?: CompanySearchResult[];
              total_results?: number | string | null;
            }
          | CompanySearchResult[];

        const list = Array.isArray(payload)
          ? payload
          : payload.items ?? payload.results ?? [];

        const headerTotal = Number(
          res.headers.get('x-total-count') ??
            res.headers.get('x-total') ??
            res.headers.get('x-total-results')
        );

        const totalFromPayload = Array.isArray(payload)
          ? payload.length
          : (() => {
              const candidate = payload.total ?? payload.total_results ?? null;
              if (typeof candidate === 'number') {
                return candidate;
              }
              if (typeof candidate === 'string') {
                const parsed = Number(candidate);
                return Number.isFinite(parsed) ? parsed : null;
              }
              return null;
            })();

        const normalizedResults = list
          .map((item) => ({
            ...item,
            cvrnummer: item.cvrnummer?.replace(/\D+/g, '') ?? '',
            leadId: item.leadId ?? null,
            hideInSearch: Boolean(item.hideInSearch),
            ansatte_aar: (() => {
              const raw = (item as any).ansatte_aar ?? (item as any).ansatteAar;
              if (raw === undefined || raw === null) {
                return null;
              }
              const parsed = Number(raw);
              return Number.isFinite(parsed) ? parsed : null;
            })(),
            ansatte_datotil:
              (item as any).ansatte_datotil ?? (item as any).ansatteDatotil ?? null,
            productionUnitCount: Number(
              item.productionUnitCount ?? (item as any).production_unit_count ?? 0
            )
          }))
          .filter((item) => item.cvrnummer);

        const pageItemCount = normalizedResults.length;
        let derivedTotal =
          typeof totalFromPayload === 'number' && Number.isFinite(totalFromPayload) && totalFromPayload > 0
            ? totalFromPayload
            : Number.isFinite(headerTotal) && headerTotal > 0
              ? headerTotal
              : 0;

        if (!Number.isFinite(derivedTotal) || derivedTotal <= 0) {
          if (pageItemCount < normalized.limit) {
            derivedTotal = (nextPage - 1) * normalized.limit + pageItemCount;
          } else {
            derivedTotal = nextPage * normalized.limit + 1;
          }
        }

        setResults(normalizedResults);
        setTotalResults(derivedTotal);
      } catch (error: any) {
        message.error(error?.message || 'Kunne ikke søge efter virksomheder');
      } finally {
        setLoading(false);
      }
    },
    [setCurrentPage, setResults, setSearchParams, setPageSize]
  );

  const handleAssignLabel = useCallback(
    async (record: CompanySearchResult, labelId: string) => {
      setAssigningLabelFor(record.cvrnummer);
      try {
        let leadId = record.leadId ?? null;

        if (!leadId) {
          const createRes = await api(`/api/companies/${record.cvrnummer}/lead`, {
            method: 'POST'
          });
          if (!createRes.ok) {
            const err = await createRes.json().catch(() => ({}));
            throw new Error(err.error || 'Kunne ikke oprette lead før tilknytning');
          }
          const created = (await createRes.json()) as { id: string; hidden_at?: string | null };
          leadId = created.id;
          setResults((prev) =>
            prev.map((item) =>
              item.cvrnummer === record.cvrnummer
                ? {
                    ...item,
                    leadId,
                    hasVisibleLead: !created.hidden_at,
                    hasHiddenLead: Boolean(created.hidden_at)
                  }
                : item
            )
          );
        }

        if (!leadId) {
          throw new Error('Lead-id mangler');
        }

        const res = await api(`/api/leads/${leadId}/labels`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ labelId })
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Kunne ikke tilknytte label');
        }
        const attached = (await res.json()) as Label;
        setLabels((prev) => {
          const exists = prev.some((item) => item.id === attached.id);
          const next = exists
            ? prev.map((item) => (item.id === attached.id ? attached : item))
            : [...prev, attached];
          return next.sort((a, b) => a.name.localeCompare(b.name, 'da', { sensitivity: 'base' }));
        });
        message.success(`Label '${attached.name}' tilknyttet`);
        if (attached.hideInSearch) {
          setResults((prev) => prev.filter((item) => item.cvrnummer !== record.cvrnummer));
        }
        if (onLeadCreated) {
          await onLeadCreated();
        }
      } catch (error: any) {
        console.error('Failed to assign label from search', error);
        message.error(error?.message || 'Kunne ikke tilknytte label');
      } finally {
        setAssigningLabelFor(null);
      }
    },
    [onLeadCreated, setResults]
  );

  useEffect(() => {
    form.setFieldsValue({
      ...paramsValues,
      includeExistingLeads: paramsValues.includeExistingLeads ?? false,
      limit: paramsValues.limit ?? DEFAULT_PAGE_SIZE,
      minEmployees: paramsValues.minEmployees ?? undefined,
      maxEmployees: paramsValues.maxEmployees ?? undefined
    });

    const currentQuery = searchParamsKey;

    if (!currentQuery) {
      setResults([]);
      lastExecutedQueryRef.current = '';
      return;
    }

    if (currentQuery === lastExecutedQueryRef.current) {
      return;
    }

    lastExecutedQueryRef.current = currentQuery;
    void performSearch(paramsValues, { skipUpdateParams: true, page: pageFromParams });
  }, [form, paramsValues, performSearch, searchParamsKey, setResults, pageFromParams]);

  const handleReset = useCallback(() => {
    form.resetFields();
    setResults([]);
    setTotalResults(0);
    setSearchParams({}, { replace: true });
    lastExecutedQueryRef.current = '';
    setCurrentPage(1);
    setPageSize(DEFAULT_PAGE_SIZE);
  }, [form, setResults, setSearchParams, setPageSize]);

  const openDetail = useCallback(
    async (cvr: string) => {
      setDetailVisible(true);
      setDetailLoading(true);
      setDetail(null);
      setDetailCvr(cvr);
      try {
        const res = await api(`/api/companies/${cvr}`);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Kunne ikke hente detaljer');
        }
        const data = (await res.json()) as CvrSummary;
        setDetail(data);
      } catch (error: any) {
        message.error(error?.message || 'Kunne ikke hente detaljer');
      } finally {
        setDetailLoading(false);
      }
    },
    []
  );

  const openAiModal = useCallback(
    async (cvr: string) => {
      const digits = onlyDigits(cvr);
      if (!digits || digits.length !== 8) {
        message.error('CVR-nummer mangler eller er ugyldigt');
        return;
      }

      setAiModalVisible(true);
      setAiModalLoading(true);
      setAiSummary(null);
      setAiSummaryCvr(digits);
      setAiAnalyzingFor(digits);

      try {
        const res = await api(`/api/companies/${digits}/ai-summary`);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Kunne ikke hente AI-analyse');
        }
        const data = (await res.json()) as CompanyAiSummary;
        setAiSummary(data);
      } catch (error: any) {
        message.error(error?.message || 'Kunne ikke hente AI-analyse');
        setAiSummary(null);
      } finally {
        setAiModalLoading(false);
        setAiAnalyzingFor(null);
      }
    },
    []
  );

  const handleQuickCall = useCallback(
    async (record: CompanySearchResult) => {
        const rawCvr = record.cvrnummer ? record.cvrnummer.replace(/\D+/g, '') : '';
        if (!rawCvr || rawCvr.length !== 8) {
          message.error('CVR-nummer mangler eller er ugyldigt');
          return;
        }

        setQuickCallTarget(rawCvr);
        try {
          const payload = {
            company_name: record.navn || undefined,
            cvrnummer: rawCvr,
            status: record.virksomhed_status || undefined,
            branchetekst: record.branchetekst || undefined,
            kommunenavn: record.kommunenavn || undefined,
            postnummer: record.postnummer || undefined,
            postdistrikt: record.postdistrikt || undefined,
            telefon: record.telefon || undefined,
            email: record.email || undefined
          };

          const res = await api('/api/leads/quick-call', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });

          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.error || 'Kunne ikke registrere opkald');
          }

          const data = (await res.json()) as {
            leadId?: string | null;
            callAttemptCount?: number;
            hidden?: boolean;
            created?: boolean;
            lastCallAt?: string | null;
          };

          setResults((prev) =>
            prev.map((item) => {
              if (item.cvrnummer !== record.cvrnummer) {
                return item;
              }

              const callAttemptCount =
                data.callAttemptCount === undefined || data.callAttemptCount === null
                  ? item.callAttemptCount
                  : data.callAttemptCount;

              const lastCallAt = data.lastCallAt ?? item.lastCallAt;
              const hidden = Boolean(data.hidden);

              return {
                ...item,
                leadId: data.leadId ?? item.leadId,
                hasVisibleLead: hidden ? item.hasVisibleLead : true,
                hasHiddenLead: hidden ? true : item.hasHiddenLead,
                callAttemptCount,
                lastCallAt
              };
            })
          );

          const lastCallShort = formatDateTimeShort(data.lastCallAt ?? null);
          const successParts: string[] = [];
          if (data.created) {
            successParts.push(data.hidden ? 'Lead oprettet (skjult)' : 'Lead oprettet');
          }
          successParts.push('Opkald registreret');
          if (lastCallShort) {
            successParts.push(`senest ${lastCallShort}`);
          }
          message.success(successParts.join(' · '));

          if (data.created && onLeadCreated) {
            await onLeadCreated();
          }
        } catch (error: any) {
          message.error(error?.message || 'Kunne ikke registrere opkald');
        } finally {
          setQuickCallTarget(null);
        }
    },
    [onLeadCreated, setResults]
  );

  const handleTableChange = useCallback(
    (paginationConfig: TablePaginationConfig) => {
      const nextPage = paginationConfig.current ?? 1;
      const nextPageSize = paginationConfig.pageSize ?? DEFAULT_PAGE_SIZE;
      const currentValues = normalizeSearchValues(form.getFieldsValue());
      setPageSize(nextPageSize);
      void performSearch({ ...currentValues, limit: nextPageSize }, { page: nextPage });
    },
    [form, performSearch]
  );

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(Math.max(totalResults, 1) / Math.max(pageSize, 1)));
    if (currentPage > maxPage) {
      const currentValues = normalizeSearchValues(form.getFieldsValue());
      void performSearch(currentValues, { page: maxPage });
    }
  }, [totalResults, pageSize, currentPage, form, performSearch]);

  const handleCreateLead = useCallback(
    async (cvr: string) => {
      setLeadCreatingFor(cvr);
      try {
        const res = await api(`/api/companies/${cvr}/lead`, { method: 'POST' });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Kunne ikke oprette lead');
        }
        message.success('Virksomheden er oprettet som lead');
        setResults((prev) => prev.filter((item) => item.cvrnummer !== cvr));
        if (onLeadCreated) {
          await onLeadCreated();
        }
      } catch (error: any) {
        message.error(error?.message || 'Kunne ikke oprette lead');
      } finally {
        setLeadCreatingFor(null);
      }
    },
    [onLeadCreated, setResults]
  );

  const handleOpenLead = useCallback((leadId: string) => {
    setLeadDetailId(leadId);
    setLeadDetailVisible(true);
  }, []);

  const closeLeadDetail = useCallback(() => {
    setLeadDetailVisible(false);
    setLeadDetailId(null);
  }, []);

  const handleLeadDetailBack = useCallback(
    (_state?: { fromCompanySearch?: string | null }) => {
      closeLeadDetail();
    },
    [closeLeadDetail]
  );

  const handleLeadUpdated = useCallback(() => {
    if (!lastExecutedQueryRef.current) {
      return;
    }
    const rawValues = form.getFieldsValue() as SearchFormValues;
    const normalized = normalizeSearchValues(rawValues);
    if (!hasSearchCriteria(normalized)) {
      return;
    }
    void performSearch({ ...rawValues, limit: normalized.limit }, {
      skipUpdateParams: true,
      page: currentPage
    });
  }, [currentPage, form, performSearch]);

  const columns: ColumnsType<CompanySearchResult> = useMemo(
    () => [
      {
        title: 'Navn',
        dataIndex: 'navn',
        key: 'navn',
        render: (_value, record) => record.navn || '(navn mangler)'
      },
      {
        title: 'CVR',
        dataIndex: 'cvrnummer',
        key: 'cvrnummer',
        render: (value: string) => formatCvrNumber(value)
      },
      {
        title: 'Status',
        dataIndex: 'virksomhed_status',
        key: 'virksomhed_status',
        render: (value: string | null) => (
          <Tag color={statusColor(value)}>{value || 'Ukendt'}</Tag>
        )
      },
      {
        title: 'Branche',
        dataIndex: 'branchetekst',
        key: 'branchetekst',
        render: (value: string | null) => value || '—'
      },
      {
        title: 'Ansatte (seneste år)',
        dataIndex: 'ansatte_aar',
        key: 'ansatte_aar',
        render: (value: number | null | undefined) =>
          typeof value === 'number' && Number.isFinite(value) ? value : '—'
      },
      {
        title: 'Postnr.',
        dataIndex: 'postnummer',
        key: 'postnummer',
        render: (_value, record) => {
          if (!record.postnummer && !record.postdistrikt) {
            return '—';
          }
          return [record.postnummer, record.postdistrikt].filter(Boolean).join(' ');
        }
      },
      {
        title: 'Opdateret',
        dataIndex: 'opdateret_tidspunkt',
        key: 'opdateret_tidspunkt',
        render: (value: string | null) => formatUpdated(value)
      },
      {
        title: 'Brugere',
        key: 'users',
        render: (_value, record) => (
          <Button size="small" onClick={() => openUsersModal(record)} disabled={!resolveCompanyId(record)}>
            Brugere
          </Button>
        )
      },
      {
        title: 'P-numre',
        dataIndex: 'productionUnitCount',
        key: 'productionUnitCount',
        render: (value: number | null | undefined) =>
          typeof value === 'number' && Number.isFinite(value) ? value : '—'
      },
      {
        title: 'Opkald',
        dataIndex: 'callAttemptCount',
        key: 'callAttemptCount',
        render: (_value, record) => {
          if (!record.callAttemptCount) {
            return <Tag color="default">Ingen</Tag>;
          }
          const shortLabel = formatDateTimeShort(record.lastCallAt) || null;
          const fullLabel = formatDateTimeFull(record.lastCallAt) || undefined;
          return (
            <Tooltip
              title={fullLabel || 'Seneste opkaldstidspunkt er ikke tilgængeligt'}
              placement="topLeft"
            >
              <Tag color="processing">
                Forsøg: {record.callAttemptCount}
                {shortLabel ? ` · ${shortLabel}` : ''}
              </Tag>
            </Tooltip>
          );
        }
      },
      {
        title: 'Label',
        key: 'assignLabel',
        render: (_value, record) => {
          const items = Array.isArray(labelMenuItems) ? labelMenuItems : [];
          const menu: MenuProps = {
            items,
            onClick: ({ key }) => handleAssignLabel(record, String(key))
          };
          const disabled = labelsLoading || items.length === 0;
          return (
            <Dropdown menu={menu} trigger={['click']}>
              <Button
                size="small"
                disabled={disabled}
                loading={assigningLabelFor === record.cvrnummer}
              >
                Tildel label
              </Button>
            </Dropdown>
          );
        }
      },
      {
        title: 'Handlinger',
        key: 'actions',
        render: (_value, record) => (
          <Space>
            <Tooltip title="Vis detaljer">
              <Button
                aria-label="Vis detaljer"
                icon={<EyeOutlined />}
                onClick={() => openDetail(record.cvrnummer)}
                shape="circle"
                size="small"
                type="text"
              />
            </Tooltip>
            <Tooltip title="AI-analyse">
              <Button
                aria-label="AI-analyse"
                icon={<RobotOutlined />}
                onClick={() => openAiModal(record.cvrnummer)}
                shape="circle"
                size="small"
                type="default"
                loading={aiAnalyzingFor === onlyDigits(record.cvrnummer)}
              />
            </Tooltip>
            {record.leadId ? (
              <Tooltip title="Gå til lead">
                <Button
                  aria-label="Gå til lead"
                  onClick={() => handleOpenLead(record.leadId!)}
                  size="small"
                  type="primary"
                  style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                >
                  Vis lead
                </Button>
              </Tooltip>
            ) : (
              <Tooltip title="Opret lead">
                <Button
                  aria-label="Opret lead"
                  icon={<PlusOutlined />}
                  onClick={() => handleCreateLead(record.cvrnummer)}
                  loading={leadCreatingFor === record.cvrnummer}
                  shape="circle"
                  size="small"
                  type="primary"
                />
              </Tooltip>
            )}
            <Tooltip title="Registrer opkald">
              <Button
                aria-label="Registrer opkald"
                icon={<PhoneOutlined />}
                onClick={() => handleQuickCall(record)}
                loading={quickCallTarget === record.cvrnummer}
                shape="circle"
                size="small"
                type="default"
              />
            </Tooltip>
          </Space>
        )
      }
    ],
    [
      assigningLabelFor,
      handleAssignLabel,
      handleCreateLead,
      handleOpenLead,
      openAiModal,
      handleQuickCall,
      labelMenuItems,
      labelsLoading,
      leadCreatingFor,
      openDetail,
      quickCallTarget,
      aiAnalyzingFor,
      openUsersModal,
      resolveCompanyId
    ]
  );

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <Card>
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Title level={4} style={{ marginBottom: 0 }}>
            Søg i virksomheder
          </Title>
          <Text type="secondary">
            Udfyld et eller flere felter for at slå virksomheder op via Datafordeler.
          </Text>
          <Form
            layout="vertical"
            form={form}
            onFinish={performSearch}
            disabled={loading}
            initialValues={{ includeExistingLeads: false, limit: DEFAULT_PAGE_SIZE, maxEmployees: 999 }}
          >
            <Row gutter={16}>
              <Col xs={24} md={12} lg={8}>
                <Form.Item name="name" label="Virksomhedsnavn">
                  <Input placeholder="F.eks. Acme A/S" allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8}>
                <Form.Item name="cvr" label="CVR-nummer">
                  <Input placeholder="F.eks. 12345678" allowClear maxLength={20} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8}>
                <Form.Item name="status" label="Status">
                  <Input placeholder="F.eks. Aktiv" allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8}>
                <Form.Item name="branche" label="Branche">
                  <Select
                    allowClear
                    showSearch
                    placeholder="Vælg branche"
                    loading={brancheLoading}
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      (option?.label as string)
                        ?.toLocaleLowerCase()
                        .includes(input.toLocaleLowerCase()) ?? false
                    }
                    options={brancheOptions.map((option) => ({
                      value: option.code || option.text,
                      label: option.code
                        ? `${option.text} (${option.code})`
                        : option.text
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8}>
                <Form.Item name="postal" label="Postnr./by">
                  <Input placeholder="F.eks. 2100 eller København" allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8}>
                <Form.Item name="municipality" label="Kommune">
                  <Input placeholder="F.eks. København" allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8}>
                <Form.Item name="minEmployees" label="Min. antal ansatte">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="F.eks. 10" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8}>
                <Form.Item name="maxEmployees" label="Max. antal ansatte">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="F.eks. 250" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8}>
                <Form.Item
                  name="limit"
                  label="Resultater pr. søgning"
                  tooltip="Vælg hvor mange virksomheder der vises pr. søgning."
                  rules={[{ type: 'number', min: 1 }]}
                >
                  <InputNumber min={1} max={MAX_PAGE_SIZE} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              name="includeExistingLeads"
              valuePropName="checked"
              style={{ marginBottom: 16 }}
            >
              <Checkbox>Medtag virksomheder som allerede er oprettet som leads</Checkbox>
            </Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                Søg
              </Button>
              <Button onClick={handleReset}>Nulstil</Button>
            </Space>
          </Form>
        </Space>
      </Card>

      <Card bodyStyle={{ padding: 0 }}>
        <Table
          rowKey="cvrnummer"
          columns={columns}
          dataSource={results}
          loading={loading}
          locale={{ emptyText: loading ? <Spin /> : <Empty description="Ingen resultater" /> }}
          pagination={{
            current: currentPage,
            pageSize,
            total: totalResults,
            hideOnSinglePage: true,
            showSizeChanger: false
          }}
          onChange={handleTableChange}
        />
      </Card>

      <Modal
        title="Lead-detaljer"
        open={leadDetailVisible}
        onCancel={closeLeadDetail}
        footer={null}
        width={960}
        destroyOnClose
      >
        {leadDetailId ? (
          <LeadDetailPage
            key={leadDetailId}
            canManage={canManage}
            onBack={handleLeadDetailBack}
            onLeadUpdated={handleLeadUpdated}
            leadIdOverride={leadDetailId}
            locationStateOverride={leadDetailLocationState}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        )}
      </Modal>

      <Modal
        title={detail ? detail.navn || formatCvrNumber(detailCvr || '') : 'Virksomhedsdetaljer'}
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={720}
        destroyOnClose
      >
        {detailLoading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : detail ? (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="CVR">{formatCvrNumber(detail.cvrnummer || detailCvr || '')}</Descriptions.Item>
            <Descriptions.Item label="Status">{detail.virksomhed_status || 'Ukendt'}</Descriptions.Item>
            <Descriptions.Item label="Branchekode">{detail.branchekode || '—'}</Descriptions.Item>
            <Descriptions.Item label="Branchetekst">{detail.branchetekst || '—'}</Descriptions.Item>
            <Descriptions.Item label="Ansatte (seneste år)">
              {typeof detail.ansatte_aar === 'number' && Number.isFinite(detail.ansatte_aar)
                ? detail.ansatte_aar
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Ansatte opdateret til">
              {detail.ansatte_datotil || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Adresse">{buildAddress(detail) || '—'}</Descriptions.Item>
            <Descriptions.Item label="Kommune">{detail.kommunenavn || '—'}</Descriptions.Item>
            <Descriptions.Item label="Telefon">{detail.telefon || '—'}</Descriptions.Item>
            <Descriptions.Item label="E-mail">{detail.email || '—'}</Descriptions.Item>
            <Descriptions.Item label="Opdateret">{formatUpdated(detail.opdateret_tidspunkt)}</Descriptions.Item>
          </Descriptions>
        ) : (
          <Empty description="Ingen data" />
        )}
      </Modal>

      <Modal
        title={`AI-analyse ${formatCvrNumber(aiSummary?.cvrnummer || aiSummaryCvr || '')}`}
        open={aiModalVisible}
        onCancel={() => {
          setAiModalVisible(false);
          setAiSummary(null);
        }}
        footer={null}
        width={720}
        destroyOnClose
      >
        {aiModalLoading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : aiSummary?.ai_summary_text ? (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space direction="vertical" size={4}>
              {aiSummary.ai_summary_model ? (
                <Text type="secondary">Model: {aiSummary.ai_summary_model}</Text>
              ) : null}
              {aiModalGeneratedLabel ? (
                <Text type="secondary">Senest genereret {aiModalGeneratedLabel}</Text>
              ) : null}
            </Space>
            {aiModalSections.length > 0 ? (
              aiModalSections.map((section: string, index: number) => (
                <Paragraph
                  key={`ai-modal-section-${index}`}
                  style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}
                >
                  {section}
                </Paragraph>
              ))
            ) : (
              <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                {aiSummary.ai_summary_text.trim()}
              </Paragraph>
            )}
          </Space>
        ) : (
          <Empty description="Ingen AI-data" />
        )}

        <Modal
          title={
            usersCompany
              ? `Brugere for ${usersCompany.name || formatCvrNumber(usersCompany.cvr)}`
              : 'Brugere'
          }
          open={usersModalOpen}
          onCancel={closeUsersModal}
          footer={null}
          width={680}
          destroyOnClose
        >
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Table
              size="small"
              rowKey={(row) => row.id}
              dataSource={companyUsers}
              loading={usersLoading}
              pagination={false}
              locale={{ emptyText: usersLoading ? <Spin /> : <Empty description="Ingen brugere" /> }}
              columns={[
                {
                  title: 'Navn',
                  dataIndex: 'full_name',
                  key: 'full_name',
                  render: (value: string | null) => value || '—'
                },
                {
                  title: 'E-mail',
                  dataIndex: 'email',
                  key: 'email',
                  render: (value: string | null) => value || '—'
                },
                {
                  title: 'Rolle',
                  dataIndex: 'role',
                  key: 'role',
                  render: (value: string | null) => value || '—'
                }
              ]}
            />

            <Card size="small" title="Opret bruger" bordered>
              <Form form={userForm} layout="vertical" preserve={false} onFinish={handleCreateUser}>
                <Form.Item name="full_name" label="Navn" rules={[{ required: true, message: 'Angiv navn' }]}>
                  <Input placeholder="F.eks. Maria Hansen" />
                </Form.Item>
                <Form.Item
                  name="email"
                  label="E-mail"
                  rules={[{ required: true, type: 'email', message: 'Angiv gyldig e-mail' }]}
                >
                  <Input placeholder="bruger@firma.dk" />
                </Form.Item>
                <Form.Item name="role" label="Rolle" initialValue="REFERRER">
                  <Select
                    options={[
                      { value: 'REFERRER', label: 'Referrer' },
                      { value: 'BROKER_ADMIN', label: 'Administrator' }
                    ]}
                  />
                </Form.Item>
                <Space>
                  <Button onClick={() => userForm.resetFields()}>Nulstil</Button>
                  <Button type="primary" htmlType="submit" loading={createUserLoading} disabled={!currentUsersCompanyId}>
                    Opret bruger
                  </Button>
                </Space>
              </Form>
            </Card>
          </Space>
        </Modal>
      </Modal>
    </Space>
  );
}
